// eWings Abroad design tokens.
//
// Every eWings front-component pulls colour, type and spacing from here, so the
// app reads as one deliberate product rather than a set of separate screens.
// Nothing in this file is Twenty's palette: these are eWings' own.
//
// The palette is built around a deep "passport blue" (trust, official process)
// with a warm saffron accent for calls to action, plus semantic colours that
// match the funnel stages already defined on the Student Lead object.

export type ColorScheme = 'light' | 'dark';

// Taken from the live site (ewings.co.in, :root custom properties), not invented:
//   --navy: #071c3c   --navy-light: #162a4b   --pink/--accent: #ff2d7a
//   --ring: #ff2d7a   --surface: #f8f9fc      --foreground: #111827
// The logo itself uses crimson #ff1d4d and wordmark navy #263e64.
export const BRAND = {
  /** Primary. eWings navy — headers, the sidebar, primary surfaces. */
  navy: '#071c3c',
  navyDeep: '#04122a',
  navyLight: '#162a4b',
  /** Wordmark navy, a touch lighter than the brand navy. */
  navyWord: '#263e64',
  /** Accent. eWings navy blue — CTAs, focus rings. */
  crimson: '#071c3c',
  crimsonLogo: '#ff1d4d',
  crimsonDeep: '#04122a',
  /** Supporting partner-university gold, used sparingly for highlights. */
  gold: '#c98d1e',
} as const;

/** Funnel stage colours, aligned 1:1 with studentLead.stage select values. */
export const STAGE_COLORS: Record<string, string> = {
  NEW_LEAD: '#6b7280',
  QUALIFIED: '#162a4b',
  COUNSELLING_SCHEDULED: '#3b5f9e',
  COUNSELLING_DONE: '#c98d1e',
  CONVERTED: '#071c3c',
  LOST: '#9ca3af',
};

/** Lead score colours, aligned with the HOT / WARM / COLD scoring output. */
export const SCORE_COLORS: Record<string, string> = {
  HOT: '#071c3c',
  WARM: '#c98d1e',
  COLD: '#6b7280',
};

/**
 * Depth, lifted from the site's own box-shadow values
 * (0 10px 25px #0000000d, 0 15px 30px #0000001a).
 */
const ELEVATION_CARD = '0 10px 25px rgba(0, 0, 0, 0.05)';
const ELEVATION_RAISED = '0 15px 30px rgba(0, 0, 0, 0.10)';

type PaletteShape = {
  brand: string;
  brandDeep: string;
  brandSoft: string;
  accent: string;
  accentDeep: string;
  gold: string;

  pageBg: string;
  surface: string;
  surfaceSunken: string;

  text: string;
  textMuted: string;
  textFaint: string;
  textOnBrand: string;

  border: string;
  borderStrong: string;

  success: string;
  warning: string;
  danger: string;

  shadow: string;
  shadowRaised: string;
};

const LIGHT: PaletteShape = {
  brand: BRAND.navy,
  brandDeep: BRAND.navyDeep,
  brandSoft: '#eef1f6',
  accent: BRAND.crimson,
  accentDeep: BRAND.crimsonDeep,
  gold: BRAND.gold,

  // --surface / --card / --foreground / --border, straight from the site.
  pageBg: '#f8f9fc',
  surface: '#ffffff',
  surfaceSunken: '#f2f4f8',

  text: '#111827',
  textMuted: '#6b7280',
  textFaint: '#9ca3af',
  textOnBrand: '#ffffff',

  border: '#e5e7eb',
  borderStrong: '#cbced4',

  success: '#15803d',
  warning: BRAND.gold,
  danger: BRAND.crimsonDeep,

  shadow: ELEVATION_CARD,
  shadowRaised: ELEVATION_RAISED,
};

const DARK: PaletteShape = {
  brand: '#3b82f6',
  brandDeep: '#040d1a',
  brandSoft: '#0a1c36',
  accent: '#3b82f6',
  accentDeep: '#1d4ed8',
  gold: '#d9a441',

  pageBg: '#030a16',
  surface: '#061325',
  surfaceSunken: '#02060e',

  text: '#f1f5f9',
  textMuted: '#94a3b8',
  textFaint: '#64748b',
  textOnBrand: '#ffffff',

  border: '#132b4d',
  borderStrong: '#1c3d6c',

  success: '#34a35c',
  warning: '#d9a441',
  danger: '#ef4444',

  shadow: '0 10px 25px rgba(2, 6, 14, 0.60)',
  shadowRaised: '0 15px 30px rgba(2, 6, 14, 0.75)',
};

export const getPalette = (scheme: ColorScheme) => (scheme === 'dark' ? DARK : LIGHT);

export type Palette = PaletteShape;

/**
 * eWings uses Satoshi on the website (--font-display / --font-body). It is not
 * bundled with Twenty, so we name it first and fall back to the system stack
 * that Twenty's shell already uses — the app matches the brand where the font
 * is available and stays visually consistent where it is not.
 */
export const FONT_FAMILY =
  'Satoshi, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Type scale. These values were realigned to Twenty's own shell tokens
 * (see shell-tokens.ts) so the eWings pages sit at the same optical weight as
 * the record tables they render beside. Earlier values ran 2-4px larger and
 * up to weight 700, which Twenty never uses -- that mismatch is what made the
 * app look like two products stitched together.
 */
export const TYPE = {
  // 1.23rem / 600, matching --t-font-size-lg.
  display: { fontSize: '1.23rem', fontWeight: 600, letterSpacing: '-0.2px', lineHeight: 1.3 },
  // 0.92rem / 600, matching --t-font-size-sm.
  title: { fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.4 },
  sectionLabel: {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
  },
  // 1.54rem / 600, matching --t-font-size-xl.
  metric: { fontSize: '1.54rem', fontWeight: 600, letterSpacing: '-0.3px', lineHeight: 1.2 },
  // 0.85rem is --t-font-size-xs, the shell's workhorse body size.
  body: { fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.5 },
  bodyStrong: { fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.5 },
  caption: { fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.4 },
  micro: { fontSize: '0.625rem', fontWeight: 400, lineHeight: 1.4 },
} as const;

/** 4px steps, matching --t-spacing-N. */
export const SPACE = {
  xs: '4px',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
} as const;

/** Matching --t-border-radius-*. Twenty's md is 8px, not 10-12px. */
export const RADIUS = {
  sm: '4px',
  md: '8px',
  lg: '8px',
  xl: '16px',
  pill: '999px',
} as const;

/**
 * The site's signature card treatment: a faint 145° tint of the accent colour
 * over the surface, rather than flat fills. Taken from the live stylesheet,
 * e.g. linear-gradient(145deg,#ff2d7a1a,#d4183d33).
 */
export const tintedGradient = (from: string, to: string): string =>
  `linear-gradient(145deg, ${from}1a, ${to}33)`;

/** The site's glow, used on accented cards: 0 0 15px #ff2d7a33. */
export const glow = (color: string): string => `0 0 15px ${color}33`;

/** Depth scale lifted from the site's box-shadow values. */
export const ELEVATION = {
  card: ELEVATION_CARD,
  raised: ELEVATION_RAISED,
  glass: '0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px rgba(255, 255, 255, 0.25)',
} as const;

